import type { DossierState } from '@/features/common/types/DossierState';
import type { CommonKey } from '@/i18n/dictionaries/common';

/**
 * The approval states a dossier is named and coloured for — in "Cererile mele", on the dossier
 * page and in the event-draft wizard.
 *
 * A dossier reaches these screens in whatever state its workflow is in, including one authored in
 * the workflow designer that this file has never heard of — so a code from the server is a string
 * until isDossierState narrows it, and an unknown one is shown as itself.
 */
export const DOSSIER_STATE = {
  draft: 'DRAFT',
  pending: 'PENDING',
  pendingValidation: 'PENDING_VALIDATION',
  published: 'PUBLISHED',
  // The event lifecycle's own terminal states, named here so they are named everywhere: without
  // them stateLabelKey returned null and the approval queue printed the raw code — an approver
  // read "CONCLUDED" in English, in capitals, on an otherwise Romanian screen.
  concluded: 'CONCLUDED',
  archived: 'ARCHIVED',
  // Advisor validation's own "yes". It was the last state reaching a screen without a name of its
  // own, so it printed itself: an approver read "VALIDATED", in English capitals, on a Romanian
  // page — the same gap CONCLUDED and ARCHIVED had.
  validated: 'VALIDATED',
  rejected: 'REJECTED',
  withdrawn: 'WITHDRAWN',
  // The event lifecycle's own "called off". It reached "Cererile mele" with no name here, so the row
  // printed CANCELLED — the gap CONCLUDED, ARCHIVED and VALIDATED each had in turn.
  cancelled: 'CANCELLED',
  // Where a dossier ends when the thing it was about — an event, an article — is deleted. Terminal,
  // and nobody's decision: not approved, not refused, not the author's own withdrawal.
  deleted: 'DELETED',
} as const;

/** Whether an (upper-cased) state code from the server is one of DOSSIER_STATE — never cast. */
export function isDossierState(code: string): code is DossierState {
  return Object.values<string>(DOSSIER_STATE).includes(code);
}

// Badge labels are intentionally English (per design), unlike the rest of the page's
// Romanian copy — do not "fix" this back to Ciornă/În examinare without checking the design.
// Shared with DossierDetailPage.tsx so the list and the detail page never disagree on what a
// given state looks like. PENDING_VALIDATION is advisor-validation's own name for "Pending".
/**
 * The dictionary key naming each state, not the word itself.
 *
 * These badges were English on an otherwise Romanian page — "Draft", "Pending", "Withdrawn" under
 * "Cererile mele" — and a test pinned that as agreed behaviour after the wording changed several
 * times on the original ticket. It was asked for again, in Romanian, so the decision moved; going
 * through the dictionary rather than hard-coding the new words is what makes the page answer the
 * language switch at all, which neither version did before.
 */
export const STATE_LABELS: Record<DossierState, CommonKey> = {
  DRAFT: 'stateDraft',
  PENDING: 'statePending',
  PENDING_VALIDATION: 'statePending',
  PUBLISHED: 'stateApproved',
  CONCLUDED: 'stateConcluded',
  ARCHIVED: 'stateArchived',
  VALIDATED: 'stateValidated',
  REJECTED: 'stateRejected',
  WITHDRAWN: 'stateWithdrawn',
  CANCELLED: 'stateCancelled',
  DELETED: 'stateDeleted',
};

/**
 * The key naming a state code as the server sends it, in any case — or null for a state this app
 * does not know, which the caller shows as its own code.
 */
export function stateLabelKey(code: string): CommonKey | null {
  const state = code.toUpperCase();
  return isDossierState(state) ? STATE_LABELS[state] : null;
}

export function isDraftState(currentState: string): boolean {
  return currentState.toUpperCase() === DOSSIER_STATE.draft;
}

/**
 * Whether a dossier is waiting on a decision. Advisor validation names that state
 * PENDING_VALIDATION rather than PENDING, and both are "În examinare" on every badge — so both
 * are pending here, or a dossier would wear the badge and miss the filter that carries its name.
 */
export function isPendingState(currentState: string): boolean {
  const state = currentState.toUpperCase();
  return state === DOSSIER_STATE.pending || state === DOSSIER_STATE.pendingValidation;
}
